import React, { useEffect } from 'react';
import cx from 'classnames';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useNotification } from 'src/context/NotificationContext';
import EventTracker from 'src/pendo/EventTracker';
import { useCall } from 'src/customer-call-sdk';
import * as Sentry from '@sentry/react';
import UserLayout from '../components/UserLayout';
import SEO from '../components/common/SEO';
import { SITE_DESCRIPTION } from '../config/seo.constants';
import withAuth from '../hoc/withAuth';
import SelfView from '../components/SelfView';
import SideBox from '../components/SideBox';
import { hideNumberSelectionDialog } from '../store/customer/actions';
import { RootState } from '../store';
import { CustomerState } from '../store/customer/types';
import withCallConnected from '../hoc/withCallConnected';
import NumberSelectionDialog from '../components/NumberSelectionDialog';
import regionConfig from '../config';
import useUser from '../hooks/useUser';

declare global {
    interface Window {
        pendoVisitorId: string;
        pendo: any;
    }
}

const StyledHome = styled(UserLayout)`
    .content {
        padding: 0 !important;
        margin: 0;
        overflow: hidden;
        background-color: #1d1d1d;
    }
    .app-container {
        margin: 4.5rem 1.5rem 2rem;
        height: calc(100vh - 6.5rem);
    }

    .side-bar-container {
        width: 420px;
        min-width: 420px;
        border-radius: 0.625rem;
    }
    .self-view-container {
        border-radius: 0.625rem;
    }
`;

const Home = () => {
    const dispatch = useDispatch();
    const user = useUser();
    const { requestNotificationPermission } = useNotification();
    const { numberSelectionDialogVisible, customerNumbers, activeCustomerNumber } = useSelector<
        RootState,
        CustomerState
    >((state) => state.customerDetail);

    const { twilioRoom, hangup } = useCall();

    const closeNumberSelectionDialog = () => {
        dispatch(hideNumberSelectionDialog());
    };

    useEffect(() => {
        if (twilioRoom && twilioRoom.state !== 'disconnected') {
            twilioRoom.disconnect();
            hangup();

            Sentry.captureMessage('Twilio room disconnected after navigating to Home', {
                tags: {
                    activity: 'twilio-disconnect',
                },
            });
        }
    }, [twilioRoom]);

    useEffect(() => {
        if (user && activeCustomerNumber) {
            const activeCustomerData = customerNumbers.find(
                (customerNumber) => customerNumber.number === activeCustomerNumber.number,
            );
            const totalNumberOfAccounts = customerNumbers.length;
            if (activeCustomerData) {
                EventTracker.handleUserIdentification(
                    user,
                    activeCustomerData,
                    totalNumberOfAccounts,
                );
            }
        }
    }, [user, customerNumbers, activeCustomerNumber]);

    useEffect(() => {
        if (customerNumbers.length === 2) {
            const { complimentaryCreditPlanName } = regionConfig;
            const extnNumber = customerNumbers.find((number) => number.numberTypeId === 'extn');
            const vriNumber = customerNumbers.find(
                (number) =>
                    number.creditPlanName === complimentaryCreditPlanName && number.minsPaid <= 0,
            );

            if (extnNumber && vriNumber) {
                EventTracker.trackEvent('complimentaryCreditPlanWithMinsPaidZero', {
                    minLimitType: vriNumber.minLimitType,
                    numberTitle: vriNumber.numberTitle,
                    ownedByType: vriNumber.ownedByType,
                    ownedByDesc: vriNumber.ownedByDesc,
                    ownedByName: vriNumber.ownedByName,
                    ownedByEmail: vriNumber.ownedByEmail,
                    numberTypeId: vriNumber.numberTypeId,
                    creditPlanName: vriNumber.creditPlanName,
                    number: vriNumber.number,
                    creditGroupName: vriNumber.creditGroupName,
                    minsPaid: vriNumber.minsPaid,
                    creditType: vriNumber.creditType,
                    creditPlanIdOrGroupId: vriNumber.creditPlanIdOrGroupId,
                    billingCode: vriNumber.billingCode,
                });
            } else {
                console.warn('Either extnNumber or vriNumber is null.');
            }
        } else {
            console.warn('Customer does not meet the criteria.');
        }
    }, [activeCustomerNumber]);

    useEffect(() => {
        requestNotificationPermission();
    }, []);

    return (
        <StyledHome contentClassName="content">
            <SEO
                title="Customer Call"
                description={`${regionConfig.appName} Customer Call -${SITE_DESCRIPTION}`}
            />
            <div className={cx('flex', 'app-container')}>
                <div className="side-bar-container bg-white mx-2">
                    <SideBox />
                </div>
                <div className="self-view-container flex-grow bg-black mx-2">
                    <SelfView />
                </div>
            </div>
            <NumberSelectionDialog
                open={numberSelectionDialogVisible}
                onClose={closeNumberSelectionDialog}
            />
        </StyledHome>
    );
};

const CallConnectedHome = withCallConnected(Home);

export default withAuth(CallConnectedHome);
